import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import organization from '../content/organization.json';
import { Building2, MapPin, Mail, Users, GraduationCap, Shield, Factory, Clock, Hammer, TreeDeciduous, ShoppingCart, Home, Warehouse, Car, Plane, TreePine, Flower2, ArrowRight, Sparkles, Network, CheckCircle } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

// Animation hook
const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInView(true);
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isInView];
};

export default function EtablissementsPage() {
  const [heroRef, heroInView] = useInView();
  const [mainRef, mainInView] = useInView();
  const [branchesRef, branchesInView] = useInView();

  const territories = [
    { name: 'Guadeloupe', code: '971', color: 'from-green-500 to-emerald-500' },
    { name: 'Martinique', code: '972', color: 'from-blue-500 to-cyan-500' },
    { name: 'Guyane', code: '973', color: 'from-purple-500 to-pink-500' },
    { name: 'La Réunion', code: '974', color: 'from-orange-500 to-amber-500' },
    { name: 'Mayotte', code: '976', color: 'from-rose-500 to-red-500' }
  ];

  const branches = [
    { id: 'batiscop', name: "BATI'SCOP", desc: 'Construction neuve et Réhabilitation', icon: Hammer, gradient: 'from-amber-500 to-orange-600', services: ['Construction neuve', 'Réhabilitation', 'Rénovation énergétique'] },
    { id: 'oscop-industrie-bois', name: "O'SCOP Industrie bois", desc: 'Produits finis pour l\'habitat', icon: TreeDeciduous, gradient: 'from-lime-500 to-green-600', services: ['Menuiserie', 'Charpente', 'Transformation du bois'] },
    { id: 'bricoscop', name: "BRICO'SCOP", desc: 'Plateforme OMNICANAL', icon: ShoppingCart, gradient: 'from-pink-500 to-rose-600', services: ['Vente en ligne', 'Points de vente', 'Conseil clients'] },
    { id: 'oscop-ge', name: "O'SCOP GE", desc: 'Groupement d\'Employeurs', icon: Users, gradient: 'from-purple-500 to-indigo-600', services: ['Mise à disposition', 'Gestion RH', 'Paie'], link: '/oscop-ge' },
    { id: 'formascop', name: "FORMA'SCOP", desc: 'Centre de formation', icon: GraduationCap, gradient: 'from-blue-500 to-cyan-600', services: ['Formations', 'Certifications', 'VAE'], link: '/formascop' },
    { id: 'immoscop', name: "IMMO'SCOP", desc: 'Habitat solidaire', icon: Home, gradient: 'from-indigo-500 to-purple-600', services: ['Programmes immobiliers', 'Habitat solidaire', 'Accession sociale'] },
    { id: 'logiscop', name: "LOGI'SCOP", desc: 'Logistique', icon: Warehouse, gradient: 'from-orange-500 to-red-600', services: ['Transport', 'Entreposage', 'Gestion des flux'], link: '/logistique-mobilite' },
    { id: 'oscop-mobilite', name: "O'SCOP Mobilité", desc: 'Transport de personnes', icon: Car, gradient: 'from-cyan-500 to-blue-600', services: ['Transport collectif', 'Covoiturage', 'Navettes'] },
    { id: 'oscop-airways', name: "O'SCOP Airways", desc: 'Mobilité aérienne', icon: Plane, gradient: 'from-sky-500 to-blue-600', services: ['Vols inter-îles', 'Fret aérien', 'Charter'] },
    { id: 'hortiscop', name: "HORTI'SCOP", desc: 'Horticulture', icon: TreePine, gradient: 'from-teal-500 to-emerald-600', services: ['Production végétale', 'Pépinières', 'Aménagement'] },
    { id: 'floralscop', name: "FLORAL'SCOP", desc: 'Fleuristerie', icon: Flower2, gradient: 'from-rose-500 to-pink-600', services: ['Compositions florales', 'Événementiel', 'Décoration'] },
    { id: 'agroscop', name: "AGRO'SCOP", desc: 'Agro-transformation', icon: Factory, gradient: 'from-emerald-500 to-green-600', services: ['Transformation', 'Circuits courts', 'Valorisation'], link: '/agro-vegetal' },
  ];

  const solidarityResources = [
    { id: 'coppam', name: 'COPPAM', subtitle: 'Partenaire de l’écosystème', desc: 'Accueil, prévention et accompagnement solidaire. La coopération avec O’SCOP est organisée par convention, avec des responsabilités propres à chaque structure.', icon: Shield, gradient: 'from-green-500 to-emerald-600', scope: 'Partenaire distinct de la SCIC', link: '/garanties/coppam' },
    { id: 'fogedom', name: 'FOGEDOM-SCIC', subtitle: 'Fonds interne de la SCIC', desc: 'Fonds de soutien, de prévention et d’ingénierie constitué au sein d’OBJECTIF SCOP OUTREMER, sans personnalité juridique distincte.', icon: Building2, gradient: 'from-red-500 to-orange-600', scope: 'Section analytique interne à O’SCOP', link: '/fogedom/presentation' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-[70vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1694702740570-0a31ee1525c7?w=1920&q=80" 
            alt="Modern office building" 
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/90 to-slate-950" />
        </div>
        
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border border-purple-500/30 mb-6 px-4 py-2 font-semibold">
              <Network className="w-4 h-4 mr-2" />
              Réseau coopératif ultramarin
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-purple-100 to-white bg-clip-text text-transparent">
                Établissements et branches
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mb-10 leading-relaxed">
              Découvrez les branches sectorielles de la SCIC O'SCOP et leurs domaines d’activité
              pour les projets des <span className="text-amber-400 font-semibold">territoires ultramarins</span>.
            </p>

            {/* Territories badges */}
            <div className="flex flex-wrap gap-3">
              {territories.map((territory, index) => (
                <div 
                  key={index}
                  className="group flex items-center gap-3 px-4 py-3 rounded-2xl backdrop-blur-xl border bg-slate-800/50 border-slate-700/50 transition-all duration-300 hover:-translate-y-1 cursor-default"
                >
                  <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${territory.color}`} />
                  <span className="font-medium text-white">{territory.name}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-400 max-w-3xl mt-5">
              Un projet dans l’un de ces territoires ? Contactez-nous pour préciser les prestations,
              les interlocuteurs et les modalités d’intervention disponibles.
            </p>
          </div>
        </div>
      </section>

      {/* Main Establishment (Siege) */}
      <section 
        ref={mainRef}
        className="py-16 bg-gradient-to-b from-slate-950 to-slate-900"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`transition-all duration-1000 ${mainInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-800/40 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-slate-700/50 overflow-hidden">
              {/* Glow effect */}
              <div className="absolute -top-20 -right-20 w-[300px] h-[300px] bg-amber-500/20 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-2xl shadow-amber-500/25">
                    <Building2 className="w-10 h-10 text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/30 mb-3 font-semibold">Siège Social</Badge>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                      {organization.legalName}
                    </h2>
                    <p className="text-lg text-slate-300 mb-6">
                      Centre de coordination de l'ensemble des activités coopératives dans les Outre-mer.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                          <MapPin className="w-4 h-4" /> Localisation
                        </h4>
                        <p className="text-slate-300">{organization.postalCode} {organization.locality}, {organization.region}</p>
                        <a href="/mentions-legales" className="text-amber-400 underline underline-offset-4 text-sm">Coordonnées dans les mentions légales</a>
                      </div>
                      <div>
                        <h4 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" /> Services
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {['Direction générale', 'Administration', 'Coordination'].map((s, i) => (
                            <Badge key={i} className="bg-slate-700 text-white/50 text-slate-300 border-slate-600/50 font-semibold">{s}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 12 Branches */}
      <section 
        ref={branchesRef}
        className="py-24 bg-slate-900 relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-r from-purple-600/5 to-blue-600/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 mb-4 px-4 py-2 font-semibold">
              <Sparkles className="w-4 h-4 mr-2" />
              12 branches prévues aux statuts
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Un réseau <span className="text-emerald-400">complet</span> et <span className="text-amber-400">spécialisé</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              Les statuts du 10 août 2026 situent le site d’exploitation au 30 rue de l’Usine,
              97130 Capesterre-Belle-Eau. Ils prévoient six établissements supplémentaires à compter
              du 1er septembre 2026. Les prestations accessibles et leurs conditions sont précisées
              lors de l’étude de votre demande.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {branches.map((branch, index) => {
              const BranchCard = branch.link ? 'a' : 'article';
              return (
              <BranchCard
                key={branch.id}
                href={branch.link}
                className={`group relative block bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 transition-all duration-500 ${branch.link ? 'hover:border-slate-600 hover:-translate-y-2 hover:shadow-2xl cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900' : 'cursor-default'} ${branchesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${branch.gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg`}>
                  <branch.icon className="w-7 h-7 text-white" />
                </div>
                
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">{branch.name}</h3>
                <p className="text-sm text-slate-400 mb-4">{branch.desc}</p>
                
                <div className="flex flex-wrap gap-1">
                  {branch.services.slice(0, 3).map((service, idx) => (
                    <span key={idx} className="text-xs bg-slate-700/50 text-slate-300 px-2 py-1 rounded-full">
                      {service}
                    </span>
                  ))}
                </div>
                
                {branch.link && (
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" aria-hidden="true">
                    <ArrowRight className="w-5 h-5 text-amber-400" />
                  </div>
                )}
              </BranchCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solidarity partners and internal fund */}
      <section className="py-24 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-red-500/20 text-red-300 border border-red-500/30 mb-4 px-4 py-2 font-semibold">
              <Shield className="w-4 h-4 mr-2" />
              Partenaire et fonds interne
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Expertise et <span className="text-red-400">solidarité</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solidarityResources.map((etab, _index) => (
              <a
                key={etab.id}
                href={etab.link}
                className="group relative block bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 pb-16 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${etab.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <etab.icon className="w-8 h-8 text-white" />
                </div>
                
                <Badge className="bg-slate-700 text-white/50 text-slate-300 border-slate-600/50 mb-3 font-semibold">{etab.subtitle}</Badge>
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-amber-400 transition-colors">{etab.name}</h3>
                <p className="text-slate-400 mb-4">{etab.desc}</p>
                
                <div className="flex items-center gap-2 text-slate-500">
                  <Network className="w-4 h-4" />
                  <span className="text-sm">{etab.scope}</span>
                </div>
                
                <div className="absolute bottom-6 right-6 flex items-center gap-2 text-amber-400 font-semibold opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity">
                  En savoir plus
                  <ArrowRight className="w-4 h-4" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-24 bg-gradient-to-br from-amber-600 via-orange-600 to-amber-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Besoin de nous <span className="text-amber-200">contacter</span> ?
          </h2>
          <p className="text-xl text-white/80 mb-6">
            Nos équipes sont disponibles pour répondre à vos questions et vous accompagner dans vos projets.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <div className="flex items-center gap-2 text-white/90">
              <Clock className="w-5 h-5" />
              <span>Lun-Ven : 8h-17h</span>
            </div>
            <div className="flex items-center gap-2 text-white/90">
              <Mail className="w-5 h-5" />
              <span>contact@objectifscopoutremer.com</span>
            </div>
          </div>

          <Button 
            size="lg" 
            className="bg-white text-amber-600 hover:bg-slate-100 font-bold px-10 py-6 text-lg shadow-2xl transition-all duration-300 hover:scale-105"
            onClick={() => window.location.href = '/contact'}
          >
            Nous contacter
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
